import { Link } from "react-router-dom";
import { PublicShell, SignupCta, useSeo } from "./glossary/PublicShell";
import {
  Compass, HeartHandshake, ShieldCheck, MapPin, ClipboardList, Map, FolderClosed,
  Users, Sparkles, ArrowRight, Leaf,
} from "lucide-react";

const AUDIENCE = [
  { ic: Sparkles, t: "Newly diagnosed families", d: "You just received a diagnosis and need clear first steps — funding, tax credits, and what to do this week." },
  { ic: ClipboardList, t: "Families waiting on assessment", d: "You're in the queue and want to prepare, draft school emails, and understand your options before the report arrives." },
  { ic: FolderClosed, t: "Organized-but-overwhelmed parents", d: "You've been at this a while and want one calm place for documents, deadlines, and next steps." },
];

const STEPS = [
  { n: "1", ic: ClipboardList, t: "Tell us about your family", d: "Answer a few quick, skippable questions about your child and where you are in the journey." },
  { n: "2", ic: Map, t: "Get your personalized roadmap", d: "We turn your answers into a clear map of programs, documents, and next steps — grouped and prioritized." },
  { n: "3", ic: HeartHandshake, t: "Take it one step at a time", d: "Track tasks, gather documents, and use the AI Family Navigator to prepare questions and school emails — any time." },
];

const VALUES = [
  { ic: Compass, t: "Clarity over clutter", d: "Plain language, one step at a time — no jargon, no overwhelm." },
  { ic: ShieldCheck, t: "Careful & honest", d: "We help you understand and organize. We don't determine eligibility or guarantee funding — qualified professionals and government decision-makers do that." },
  { ic: Leaf, t: "Made in Canada", d: "Built for Canadian families, starting with Ontario support pathways and growing across the country." },
];

export default function About() {
  useSeo({
    title: "About KidPath — a calmer way to navigate autism support",
    description: "KidPath is a Canadian platform that helps families of autistic and neurodivergent children understand funding, school supports, and therapy pathways — one calm, clear step at a time. Starting in Ontario.",
  });

  return (
    <PublicShell>
      {/* HERO */}
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] pt-14 sm:pt-20 pb-6">
        <div className="max-w-3xl">
          <p className="inline-flex items-center gap-2 text-kid-teal font-semibold text-sm mb-3">
            <MapPin size={16} /> Made in Canada · Starting in Ontario
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-kid-navy leading-[1.05]">
            A calmer way to navigate autism funding &amp; support.
          </h1>
          <p className="text-kid-muted mt-5 text-base sm:text-lg leading-relaxed">
            KidPath helps parents and caregivers of autistic and neurodivergent children understand available funding,
            organize next steps, and find support options — without having to figure it all out alone.
          </p>
        </div>
      </section>

      {/* MISSION */}
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-8">
        <div className="rounded-3xl bg-kid-navy text-white p-8 sm:p-12" data-testid="about-mission">
          <p className="inline-flex items-center gap-2 text-kid-teal font-semibold text-sm mb-3">
            <HeartHandshake size={16} /> Our mission
          </p>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold leading-snug max-w-3xl text-white">
            Less overwhelm, more confidence — so families can focus on their child, not the paperwork.
          </h2>
          <p className="text-white/80 mt-4 max-w-2xl leading-relaxed">
            The autism support system in Canada is complex: funding programs, tax credits, school accommodations, therapy
            waitlists, and endless forms. We believe families deserve a clear, trustworthy guide through it — one that
            meets you where you are and turns a confusing maze into a set of calm, doable steps.
          </p>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-10">
        <div className="max-w-2xl mb-8">
          <p className="inline-flex items-center gap-2 text-kid-teal font-semibold text-sm mb-2"><Users size={16} /> Who KidPath is for</p>
          <h2 className="font-display text-3xl font-extrabold text-kid-navy">Built for families at every stage</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {AUDIENCE.map((a, i) => (
            <div key={i} className="rounded-3xl border border-kid-line bg-white p-6" data-testid="about-audience-card">
              <div className="w-12 h-12 rounded-2xl bg-kid-skytint grid place-items-center mb-4"><a.ic size={22} className="text-kid-teal" /></div>
              <h3 className="font-display text-lg font-bold text-kid-navy">{a.t}</h3>
              <p className="text-kid-muted mt-1.5 leading-relaxed text-sm">{a.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-10">
        <div className="max-w-2xl mb-8">
          <p className="inline-flex items-center gap-2 text-kid-teal font-semibold text-sm mb-2"><Compass size={16} /> How it works</p>
          <h2 className="font-display text-3xl font-extrabold text-kid-navy">Three steps to a clearer path</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <div key={i} className="rounded-3xl border border-kid-line bg-white p-6 relative" data-testid="about-step-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-kid-teal text-white grid place-items-center font-display font-extrabold">{s.n}</div>
                <s.ic size={22} className="text-kid-teal" />
              </div>
              <h3 className="font-display text-lg font-bold text-kid-navy">{s.t}</h3>
              <p className="text-kid-muted mt-1.5 leading-relaxed text-sm">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CANADA / ONTARIO FOCUS */}
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-10">
        <div className="rounded-3xl border border-kid-line bg-kid-skytint/40 p-8 sm:p-10 grid lg:grid-cols-2 gap-8 items-center" data-testid="about-canada">
          <div>
            <p className="inline-flex items-center gap-2 text-kid-teal font-semibold text-sm mb-2"><MapPin size={16} /> Canadian focus</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-kid-navy leading-snug">Starting in Ontario, built to grow across Canada</h2>
            <p className="text-kid-muted mt-4 leading-relaxed">
              KidPath begins with Ontario support pathways — the Ontario Autism Program, the Disability Tax Credit,
              the Child Disability Benefit, school supports, and more — with verified, plain-language guidance and
              official application links. We're building province by province so more families can find their way.
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            {["Ontario Autism Program", "Disability Tax Credit", "Child Disability Benefit", "IEP & school supports", "Assistance for Children with Severe Disabilities", "Special Services at Home"].map((chip) => (
              <span key={chip} className="text-sm bg-white border border-kid-line text-kid-navy px-3.5 py-2 rounded-full">{chip}</span>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-10">
        <div className="max-w-2xl mb-8">
          <p className="inline-flex items-center gap-2 text-kid-teal font-semibold text-sm mb-2"><ShieldCheck size={16} /> What we stand for</p>
          <h2 className="font-display text-3xl font-extrabold text-kid-navy">Our promise to families</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {VALUES.map((v, i) => (
            <div key={i} className="rounded-3xl border border-kid-line bg-white p-6" data-testid="about-value-card">
              <div className="w-12 h-12 rounded-2xl bg-kid-skytint grid place-items-center mb-4"><v.ic size={22} className="text-kid-teal" /></div>
              <h3 className="font-display text-lg font-bold text-kid-navy">{v.t}</h3>
              <p className="text-kid-muted mt-1.5 leading-relaxed text-sm">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-12">
        <SignupCta variant="band" testid="about-cta" />
        <p className="text-center text-sm text-kid-muted mt-6">
          Have a question first? <Link to="/contact" className="text-kid-teal font-semibold hover:underline">Get in touch <ArrowRight size={13} className="inline" /></Link>
        </p>
      </section>
    </PublicShell>
  );
}
